/**
 * PreAssignedContactsPanel
 *
 * Shows contacts that were pre-assigned to the current user (because they
 * called OUT to a previously-unassigned number). The user can Accept the
 * contact (commits to a real individual assignment) or Decline (returns
 * the number to the unassigned pool).
 *
 * Designed to be embedded at the top of the contacts list / messages page.
 */
import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Phone, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const PreAssignedContactsPanel = () => {
  const { api } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [acting, setActing] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get('/twilio/contacts/pre-assigned');
      setContacts(r.data.contacts || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!contacts.length) return null;

  const handle = async (id, action) => {
    setActing(id + action);
    try {
      const path = action === 'accept' ? 'accept-pre-assignment' : 'decline-pre-assignment';
      await api.post(`/twilio/contacts/${id}/${path}`);
      toast.success(action === 'accept' ? 'Contact saved to your list' : 'Contact returned to the pool');
      setContacts((c) => c.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-300/70 bg-amber-50 dark:bg-amber-500/10 p-3 mb-3" data-testid="pre-assigned-panel">
      <div className="flex items-center gap-2 mb-2">
        <Phone className="w-4 h-4 text-amber-700" />
        <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          New contact{contacts.length > 1 ? 's' : ''} from a recent call
        </h3>
      </div>
      <p className="text-xs text-amber-900/80 dark:text-amber-200/80 mb-2">
        Accept to add to your contacts. Decline to return to the unassigned pool.
      </p>
      <div className="space-y-2">
        {contacts.map((c) => (
          <div key={c.id} className="flex items-center justify-between gap-2 bg-white/70 dark:bg-black/30 rounded-xl px-3 py-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{c.name || c.phone_number}</p>
              <p className="text-xs text-muted-foreground truncate">{c.phone_number}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                className="h-8 px-2"
                onClick={() => handle(c.id, 'accept')}
                disabled={acting === c.id + 'accept'}
                data-testid={`pre-accept-${c.id}`}
              >
                <Check className="w-3.5 h-3.5 mr-1" />Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2"
                onClick={() => handle(c.id, 'decline')}
                disabled={acting === c.id + 'decline'}
                data-testid={`pre-decline-${c.id}`}
              >
                <X className="w-3.5 h-3.5 mr-1" />Decline
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PreAssignedContactsPanel;
